import { Observable, Subject, combineLatest, of } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { Component, OnInit } from '@angular/core'
import { Store } from '@ngrx/store'
import { SerieProposal, SerieProposalStatus } from '../serie-proposal'
import { serieProposalSelectors } from '../serie-proposal.selectors'
import { Actions } from '@ngrx/effects'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Router } from '@angular/router'
import { Sale, StatusCode } from '../../../sale/sale'
import { serieProposalActions } from '../serie-proposal.actions'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { saleActions } from '../../../sale/sale.actions'
import { saleItemSelectors } from '../../../sale/sale-item/sale-item.selectors'
import { saleSelectors } from '../../../sale/sale.selectors'
import { SaleItem } from '../../../sale/sale-item/sale-item'
import { memberSelectors } from '../../../member/member.selectors'
import { SerieProposalService } from '../serie-proposal.service'
import { serieProposalItemSelectors } from '../serie-proposal-item/serie-proposal-item.selectors'

@UntilDestroy()
@Component({
  selector: 'serie-proposal-sales-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SerieProposalSalesTabComponent implements OnInit {
  public sales$: Observable<Sale[]>
  public serieProposal$: Observable<SerieProposal>
  public serieProposal: SerieProposal
  public serieProposalId$: Observable<number>
  public removeSearchTerm$: Subject<boolean> = new Subject<boolean>()
  public total: number = 0
  public page: number = 1
  public limit: number = 10
  public pageCount: number = 0
  public isLoading: boolean = false
  public refreshData$: Subject<boolean> = new Subject<boolean>()
  public saleItems$: Observable<SaleItem[]>
  public serieProposalStatus = SerieProposalStatus

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    public serieProposalService: SerieProposalService,
    private router: Router
  ) {}

  ngOnInit() {
    this.serieProposalId$ = this.store
      .select(serieProposalSelectors.selectIdRouteParam)
      .pipe(filter((id) => Boolean(id)))

    this.serieProposal$ = this.serieProposalId$.pipe(
      tap((id) =>
        this.store.dispatch(
          serieProposalActions.fetchOneById({
            id,
            params: RequestQueryBuilder.create({
              join: [
                { field: 'customer' },
                { field: 'organization' },
                { field: 'organization.tags' },
                { field: 'serieProposalItems' },
                { field: 'serieProposalItems.saleItem' }
              ]
            })
          })
        )
      ),
      switchMap((serieProposalId) =>
        this.store.select(serieProposalSelectors.selectById, serieProposalId)
      ),
      tap((serieProposal) => (this.serieProposal = serieProposal))
    )

    this.saleItems$ = combineLatest([
      this.serieProposal$,
      this.store.select(serieProposalItemSelectors.selectEntities),
      this.store.select(saleItemSelectors.selectAll)
    ]).pipe(
      untilDestroyed(this),
      distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
      switchMap(([serieProposal, serieProposalItemEntities, allSaleItems]) => {
        const saleItemIds: number[] = serieProposal?.serieProposalItems?.map(
          (id) => serieProposalItemEntities?.[id]?.saleItemId
        )

        if (saleItemIds?.length) {
          this.store.dispatch(
            saleActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: {
                  'saleItems.id': {
                    [CondOperator.IN]: saleItemIds
                  }
                },
                join: [
                  { field: 'creditSales' },
                  { field: 'customer' },
                  { field: 'saleItems' }
                ]
              })
            })
          )
        }

        const filteredSaleItems = allSaleItems.filter((saleItem) =>
          saleItemIds.includes(saleItem.id)
        )

        return of(filteredSaleItems)
      })
    )

    this.sales$ = combineLatest([
      this.saleItems$,
      this.store.select(saleSelectors.selectEntities)
    ]).pipe(
      map(([saleItems, saleEntities]) => {
        if (!Object.keys(saleEntities).length) {
          return []
        }
        return [
          ...new Set(
            saleItems?.map((saleItem) => saleEntities[saleItem.saleId])
          )
        ]
      })
    )
  }

  public onClickRow(sale: Sale): void {
    this.router.navigateByUrl(`/sales/${sale.id}`)
  }

  public selectNewPage(page) {
    this.page = page
  }

  public selectNewLimit(limit) {
    this.limit = limit
    this.page = 1
  }
}
